import { useEffect } from "react";
import ENV from "@/utils/env";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { AssetThumbnails } from "@/features/studies/components/asset-thumbnails";
import { RegionList } from "@/features/studies/components/region-list";
import { Button } from "@/components/ui/button";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { StudiesLayout } from "@/components/layouts";
import { useGetAllPreProcessedData, useUpdateAllData } from "@/api/studio/data/queries";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { ReviewMediaSkeleton } from "@/features/studies/components/review-media-skeleton";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useSegment } from "@/hooks/use-segment";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { StudyCanvas } from "@/components/ui/canvas";
import { useModalStore } from "@/stores/use-modal-store";
import { ValidationModal } from "@/features/studies/modals/validation-modal";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";

/* Prop Types */
interface StudyAssetReviewRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * StudyAssetReviewRoute component
 * Here we render all the detected AOIs for each asset in the study
 * The user can rename, delete, or add new AOIs
 */
export const StudyAssetReviewRoute = ({ projectId, studyId }: StudyAssetReviewRouteProps) => {
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "study-review");

  /* Feature Flags */
  const isStudyEdit = useFeatureFlag("studyEdit1");

  /* Stores */
  const { open, close } = useModalStore((state) => state.modalActions);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-review");

  /* State */
  const assetIndex = useEditorStore((state) => state.assetIndex);
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);

  /* Actions */
  const { updateRegion, setAllAssetRegionData, setAssetIndex } = useEditorStore((state) => state.editorActions);

  /* Queries */
  const { mutateAsync: saveReviewData, isPending: isSaving } = useUpdateAllData();
  const { data: allStagesPreProcessedData, isLoading, isError } = useGetAllPreProcessedData(projectId, studyId);

  /* Handlers */
  const handleRegionSave = (id: string, x: number, y: number, width: number, height: number) => {
    // Round to integers
    x = Math.round(x);
    y = Math.round(y);
    width = Math.round(width);
    height = Math.round(height);

    // Create the region
    const points = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height },
    ];

    // Update the store
    updateRegion(id, points);
  };

  const handleProcess = async () => {
    // Make sure that all assets have at least one region
    const assetsWithoutRegions = allAssetRegionData
      .map((asset, index) => ({ name: asset.name, index, regions: asset.regions }))
      .filter((asset) => !asset.regions || asset.regions.length === 0);

    if (assetsWithoutRegions.length > 0) {
      // Show validation modal
      open(<ValidationModal assetsWithoutRegions={assetsWithoutRegions} onClose={close} onGoToAsset={(index) => setAssetIndex(index)} studyType="asset" />);
      return;
    }

    if (isStudyEdit) {
      try {
        await saveReviewData({
          projectId,
          studyId,
          assets: allAssetRegionData.map((asset) => ({ key: asset.key, regions: asset.regions ?? [] })),
        });
      } catch {
        return;
      }
    }

    // Segment tracking
    segmentTrackEvent("AOI Review Completed");

    // Navigate to the next step (context for asset, process for scene)
    navigateToNext();
  };

  const handleBeforeStepNavigate = async () => {
    if (!isStudyEdit) return;

    try {
      await saveReviewData({
        projectId,
        studyId,
        assets: allAssetRegionData.map((asset) => ({ key: asset.key, regions: asset.regions ?? [] })),
      });
    } catch {
      // Save failed — the mutation's onError toast already informs the user.
      // We still allow the user to navigate backwards rather than trapping them here.
    }
  };

  /* Side Effects */
  useEffect(() => {
    // save the data to the store on query completion
    // this is because we have to track changes to the data on the client side
    // and then push the final data to the server with the process button
    if (allStagesPreProcessedData) {
      setAssetIndex(0); // Reset asset index to 0 on new data
      setAllAssetRegionData(allStagesPreProcessedData);
    }
  }, [allStagesPreProcessedData, setAllAssetRegionData, setAssetIndex]);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-review" onBeforeStepNavigate={handleBeforeStepNavigate}>
      {isLoading && <ReviewMediaSkeleton uploadLength={3} />}
      {isError && <ReviewMediaSkeleton isError={isError || !allAssetRegionData.length} uploadLength={3} />}

      {!isLoading && !isError && allAssetRegionData && allAssetRegionData.length > 0 && (
        <>
          {/* Toolbar */}
          <Toolbar>
            <ToolbarItem>
              <h1 className="font-semibold">Review assets</h1>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="primary" onClick={handleProcess} disabled={isSaving}>
                Next: choose context <ChevronRightIcon className="size-4.5" />
              </Button>
            </ToolbarItem>
          </Toolbar>

          <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
            {/* Thumbnails */}
            <div className="flex flex-col gap-4 overflow-y-auto min-w-[10vw]">
              <AssetThumbnails />
            </div>

            {/* Editor */}
            <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full">
              <StudyCanvas
                handleRegionSave={handleRegionSave}
                assetSize={allAssetRegionData[assetIndex].metaData}
                imageUrl={`${ENV.API_PLATFORM_URL}/pipeline/renderer/v1/images?prefix=${projectId}/${studyId}&key=${allAssetRegionData[assetIndex].key}&maxWidth=1024`}
                projectId={projectId}
                studyId={studyId}
                assetRegions={allAssetRegionData[assetIndex].regions || []}
                studyType="asset"
              />
            </div>

            {/* Regions */}
            <div className="flex flex-col min-w-96 max-w-[25vw] pr-4 min-h-full rounded-lg">
              <RegionList projectId={projectId} studyId={studyId} studyType="asset" />
            </div>
          </div>
        </>
      )}
    </StudiesLayout>
  );
};
